import { useState, useEffect, type ChangeEvent, type ReactElement } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Mail, Loader2, Send, MessageSquareText, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

interface AlertSettings {
  enabled: boolean;
  emails: string[];
}

interface TestThreadSetting {
  thread_id: string;
}

const errorDetail = (error: unknown, fallback: string): string =>
  (axios.isAxiosError(error) && (error.response?.data as { detail?: string } | undefined)?.detail) || fallback;

export const HandoffAlertsCard = (): ReactElement => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [emailsText, setEmailsText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [sendingTest, setSendingTest] = useState<boolean>(false);

  useEffect(() => {
    axios.get<AlertSettings>(`${API}/settings/auto-responder/alerts`, { withCredentials: true })
      .then((res) => {
        setEnabled(res.data.enabled);
        setEmailsText((res.data.emails || []).join(", "));
      })
      .catch(() => toast.error("Failed to load alert settings"))
      .finally(() => setLoading(false));
  }, []);

  const parseEmails = (): string[] => emailsText.split(/[,\n;]+/).map((e) => e.trim()).filter(Boolean);

  const save = async (nextEnabled: boolean = enabled): Promise<void> => {
    setSaving(true);
    try {
      const res = await axios.put<AlertSettings>(`${API}/settings/auto-responder/alerts`,
        { enabled: nextEnabled, emails: parseEmails() }, { withCredentials: true });
      setEnabled(res.data.enabled);
      setEmailsText((res.data.emails || []).join(", "));
      toast.success(nextEnabled ? "Handoff alerts on" : "Handoff alerts saved");
    } catch (error) {
      toast.error(errorDetail(error, "Failed to save alert settings"));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async (): Promise<void> => {
    setSendingTest(true);
    try {
      const res = await axios.post<{ sent_to: string[] }>(`${API}/settings/auto-responder/alerts/test`, {}, { withCredentials: true });
      toast.success(`Test alert sent to ${res.data.sent_to.join(", ")}`);
    } catch (error) {
      toast.error(errorDetail(error, "Failed to send test alert"));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="handoff-alerts-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-amber-500" />
          Agent Handoff Alerts
        </CardTitle>
        <CardDescription className="text-slate-500">
          Email the team whenever the AI hands a live chat to a person (customer asks for a representative, or the AI can't answer)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-lg border border-slate-200 bg-slate-50">
          <div className="flex-1">
            <Label className="text-slate-900 font-medium">Send email alerts</Label>
            <p className="text-sm text-slate-500 mt-1">Delivered via AWS SES from your support sender address</p>
          </div>
          <div className="flex items-center gap-3">
            {(loading || saving) && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            <Switch checked={enabled} onCheckedChange={(v: boolean) => save(v)} disabled={loading || saving} data-testid="handoff-alerts-switch" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="alert-emails" className="text-slate-900 font-medium">Recipients</Label>
          <Input
            id="alert-emails"
            placeholder="support@talentdesk.io, ops@talentdesk.io"
            value={emailsText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailsText(e.target.value)}
            disabled={loading}
            data-testid="handoff-alert-emails-input"
          />
          <p className="text-xs text-slate-400">Separate multiple addresses with commas</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save()} disabled={loading || saving} className="gap-2" data-testid="save-alert-emails-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save recipients
          </Button>
          <Button variant="outline" onClick={sendTest} disabled={loading || sendingTest || !parseEmails().length} className="gap-2" data-testid="send-test-alert-btn">
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send test email
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const TestThreadCard = (): ReactElement => {
  const [threadId, setThreadId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    axios.get<TestThreadSetting>(`${API}/settings/auto-responder/test-thread`, { withCredentials: true })
      .then((res) => setThreadId(res.data.thread_id || ""))
      .catch(() => toast.error("Failed to load test thread setting"))
      .finally(() => setLoading(false));
  }, []);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const res = await axios.put<TestThreadSetting>(`${API}/settings/auto-responder/test-thread`, { thread_id: threadId }, { withCredentials: true });
      setThreadId(res.data.thread_id);
      toast.success(res.data.thread_id ? "Test thread saved" : "Test thread cleared");
    } catch (error) {
      toast.error(errorDetail(error, "Failed to save test thread"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="test-thread-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquareText className="w-5 h-5 text-sky-500" />
          HubSpot Test Thread
        </CardTitle>
        <CardDescription className="text-slate-500">
          Used by "Send test to HubSpot" in the Auto-Responder test dialog so you can preview real formatting. Open any internal test chat in HubSpot Inbox and copy the number at the end of the URL.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="e.g. 9876543210"
            value={threadId}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setThreadId(e.target.value.replace(/\D/g, ""))}
            disabled={loading}
            inputMode="numeric"
            data-testid="test-thread-input"
          />
          <Button onClick={save} disabled={loading || saving} className="gap-2 sm:w-auto" data-testid="save-test-thread-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
        </div>
        <p className="text-xs text-slate-400">Messages sent here are real HubSpot messages, so use a thread only your team can see.</p>
      </CardContent>
    </Card>
  );
};
